'use client';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';

export const registerSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: z.string().min(12, VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE()),
      password: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),

      gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: z.boolean().optional(),

      newsletter_return_url: z.string(),
      tos: z.boolean().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': z.string().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

type RegisterFormProps = {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
};

export function RegisterForm({ initialSocialAuthProviders }: RegisterFormProps) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof registerSchema>>>({
    resolver: zodResolver(registerSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,
      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
    },
  });
  const gdprAccepted = useWatch({ control: form.control, name: 'gdpr' });
  const newsletterAccepted = useWatch({ control: form.control, name: 'newsletter' });
  const newsletterReturnUrl = useWatch({ control: form.control, name: 'newsletter_return_url' });

  const { toast } = useToast();
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    /** Check password validity */
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    try {

      const resp = await register({ ...data,  locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text654'),
        variant: 'success',
      });
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text653'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const handlePasswordConfirmationMouseDown = () => {
    setShowPasswordConfirmation(true);
  };

  const handlePasswordConfirmationMouseUp = () => {
    setShowPasswordConfirmation(false);
  };

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col space-y-3', 'ea-register-form')}
        onSubmit={handleSubmit}
      >
        {/* header */}
        <div className={cn('mt-2 w-full max-w-[412px] px-2 py-2', 'ea-register-form__header')}>
          <h3
            className={cn('mb-4 text-center text-3xl font-bold', 'ea-register-form__header-title')}
          >
            {t('text261')}
          </h3>
          <p className={cn('mt-2 text-center', 'ea-register-form__link-wrapper')}>
            {t('text622')}{' '}
            <Link
              href={ROUTES.SIGN_IN() ?? '#'}
              className={cn('w-full text-center text-primary', 'ea-register-form__link')}
              prefetch={false}
            >
              {t('text623')}
            </Link>
          </p>
        </div>

        {/* content */}
        <SocialAuthButtons
          className="ea-register-form__social-auth w-full"
          gdprAccepted={gdprAccepted}
          initialProviders={initialSocialAuthProviders}
          newsletterAccepted={newsletterAccepted}
          newsletterReturnUrl={newsletterReturnUrl}
        />

        <div className={cn('flex flex-col space-y-1', 'ea-register-form__content')}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__name-field')}>
                <FormLabel>{t('text33')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('text33')}
                    className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="surname"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__surname-field')}>
                <FormLabel>{t('text34')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('text34')}
                    // rounded={AUTH_CONFIG.register.input.rounded}
                    className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className={cn('', 'ea-register-form__email-field')}>
                <FormLabel>{t('text35')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('text35')}
                    className={{ wrapper: cn('rounded-lg'), main: 'rounded-lg' }}
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__phone-field')}>
                <FormLabel>{t('text36')}</FormLabel>
                <FormControl>
                  <PhoneInputField {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__password-field')}>
                <FormLabel>{t('text191')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                    />
                    <button
                      type="button"
                      onMouseDown={handlePasswordMouseDown}
                      onMouseUp={handlePasswordMouseUp}
                      onMouseLeave={handlePasswordMouseUp} // In case the cursor leaves the button area while pressing
                      onTouchStart={handlePasswordMouseDown}
                      onTouchEnd={handlePasswordMouseUp}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </FormControl>
                <FormLabel
                  aria-required="false"
                  className="text-sm text-muted-foreground"
                  isRequired={false}
                >
                  {t('text630')}
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password_confirmation"
            render={({ field }) => (
              <FormItem
                required
                className={cn('', 'ea-register-form__password-confirmation-field')}
              >
                <FormLabel>{t('text263')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      className={{ wrapper: cn('rounded-lg'), main: 'rounded-lg' }}
                      type={showPasswordConfirmation ? 'text' : 'password'}
                      {...field}
                    />
                    <button
                      type="button"
                      onMouseDown={handlePasswordConfirmationMouseDown}
                      onMouseUp={handlePasswordConfirmationMouseUp}
                      onMouseLeave={handlePasswordConfirmationMouseUp} // In case the cursor leaves the button area while pressing
                      onTouchStart={handlePasswordConfirmationMouseDown}
                      onTouchEnd={handlePasswordConfirmationMouseUp}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                    >
                      {showPasswordConfirmation ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* FOOTER */}
        <div
          className={cn('flex flex-col items-start space-y-4 pb-12', 'ea-register-form__footer')}
        >
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  {t('text264')}{' '}
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.(), locale, 'tos')}
                    className="underline"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text265')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__gdpr-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(
                      ROUTES?.GDPR?.() ?? '/',
                      locale,
                      'gdpr',
                    )}
                    className="underline"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text364')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="newsletter"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__newsletter-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="border-none outline outline-1 outline-border"
                  />
                </FormControl>
                <FormLabel>{t('text629')}</FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className={cn('h-12 w-full text-lg', 'ea-register-form__submit-button')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text192')}
          </Button>
          <div>
            {/* TODO LINKS */}
            {t('text625')}{' '}
            <Link
              href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.() ?? '/', locale, 'tos')}
              className="text-primary"
              prefetch={false}
            >
              {t('text626')}
            </Link>{' '}
            {t('text627')}{' '}
            <Link
              href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr')}
              className="text-primary"
              prefetch={false}
            >
              {t('text628')}
            </Link>
          </div>
        </div>
      </form>
    </Form>
  );
}
